import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import FormModal from '../components/FormModal';
import { InlineLoader } from '../components/loading';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import SearchableSelect, { type SearchableOption } from '../components/SearchableSelect';
import MultiSearchableSelect from '../components/MultiSearchableSelect';
import { SearchInput } from '../components/SearchInput';
import { Skeleton } from '../components/Skeleton';
import {
  managerStoreAccessAPI,
  type ManagerStoreAccessRule,
  type StoreAccessCriteria,
  type StoreAccessOptions,
  type StoreAccessPreview,
  type StoreAccessStoreOption,
} from '../services/api';
import { useAuthStore } from '../stores/authStore';

/**
 * Master Data → Stores → Manager Access.
 *
 * A manager's stores normally come from their team: the stores their reportees work at.
 * Some managers also look after stores by area — a trade, a store category, a region —
 * whoever the staff there report to. A rule here tags a manager with those stores. They are
 * ADDED to the manager's team stores everywhere stores are listed (mobile store list,
 * store visits, store staff; web Stores page and Store Visits report), and the managers
 * above them see them too. A manager without a rule sees exactly what they did before.
 *
 * The preview is the safety net: before saving, the admin sees how many stores the
 * conditions match and which of them will actually show (Active, with someone deployed).
 */

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

type Mode = 'details' | 'stores';

type TextListKey = 'tradeIds' | 'categoryIds' | 'regions' | 'tiers' | 'states' | 'cities';

interface FormState {
  employeeCode: string;
  label: string;
  mode: Mode;
  tradeIds: string[];
  /** Kept as strings for the picker; sent as numbers. */
  categoryIds: string[];
  regions: string[];
  tiers: string[];
  states: string[];
  cities: string[];
  /** store_custom_fields.id → picked options */
  custom: Record<string, string[]>;
  storeIds: string[];
}

const EMPTY_FORM: FormState = {
  employeeCode: '', label: '', mode: 'details',
  tradeIds: [], categoryIds: [], regions: [], tiers: [], states: [], cities: [], custom: {}, storeIds: [],
};

const formFromRule = (rule: ManagerStoreAccessRule): FormState => {
  const c = rule.criteria;
  return {
    employeeCode: rule.employeeCode,
    label: rule.label ?? '',
    mode: c.storeIds?.length ? 'stores' : 'details',
    tradeIds: c.tradeIds ?? [],
    categoryIds: (c.categoryIds ?? []).map(String),
    regions: c.regions ?? [],
    tiers: c.tiers ?? [],
    states: c.states ?? [],
    cities: c.cities ?? [],
    custom: Object.fromEntries((c.customFields ?? []).map((f) => [f.fieldId, f.values])),
    storeIds: c.storeIds ?? [],
  };
};

/** Only the conditions of the chosen mode, with empty ones left out. */
const criteriaFromForm = (form: FormState): StoreAccessCriteria => {
  if (form.mode === 'stores') return form.storeIds.length > 0 ? { storeIds: form.storeIds } : {};
  const criteria: StoreAccessCriteria = {};
  if (form.tradeIds.length > 0) criteria.tradeIds = form.tradeIds;
  if (form.categoryIds.length > 0) criteria.categoryIds = form.categoryIds.map(Number);
  if (form.regions.length > 0) criteria.regions = form.regions;
  if (form.tiers.length > 0) criteria.tiers = form.tiers;
  if (form.states.length > 0) criteria.states = form.states;
  if (form.cities.length > 0) criteria.cities = form.cities;
  const customFields = Object.entries(form.custom)
    .filter(([, values]) => values.length > 0)
    .map(([fieldId, values]) => ({ fieldId, values }));
  if (customFields.length > 0) criteria.customFields = customFields;
  return criteria;
};

/** One readable line per condition, e.g. "Trade: LFR, MBO". */
const describeCriteria = (criteria: StoreAccessCriteria, options: StoreAccessOptions | null): string[] => {
  const named = <T extends { id: string | number; name: string }>(ids: (string | number)[], list: T[] | undefined) =>
    ids.map((id) => list?.find((item) => String(item.id) === String(id))?.name ?? 'Removed').join(', ');
  const parts: string[] = [];
  if (criteria.tradeIds) parts.push(`Trade: ${named(criteria.tradeIds, options?.trades)}`);
  if (criteria.categoryIds) parts.push(`Category: ${named(criteria.categoryIds, options?.categories)}`);
  if (criteria.regions) parts.push(`Region: ${criteria.regions.join(', ')}`);
  if (criteria.tiers) parts.push(`Tier: ${criteria.tiers.join(', ')}`);
  if (criteria.states) parts.push(`State: ${criteria.states.join(', ')}`);
  if (criteria.cities) parts.push(`City: ${criteria.cities.join(', ')}`);
  for (const field of criteria.customFields ?? []) {
    const label = options?.customFields.find((f) => f.id === field.fieldId)?.label ?? 'Custom field';
    parts.push(`${label}: ${field.values.join(', ')}`);
  }
  if (criteria.storeIds) {
    parts.push(`${criteria.storeIds.length} specific ${criteria.storeIds.length === 1 ? 'store' : 'stores'}`);
  }
  return parts;
};

/** Picker options, plus any value already on the rule that the list no longer offers. */
const optionsWithSelected = (options: SearchableOption[], selected: string[]): SearchableOption[] => {
  const known = new Set(options.map((o) => o.value));
  const missing = selected.filter((v) => !known.has(v)).map((v) => ({ value: v, label: v }));
  return missing.length > 0 ? [...options, ...missing] : options;
};

const textOptions = (values: string[]): SearchableOption[] => values.map((v) => ({ value: v, label: v }));

export default function ManagerStoreAccessPage({
  embedded, actionSlot,
}: { embedded?: boolean; actionSlot?: HTMLElement | null } = {}) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  const [rules, setRules] = useState<ManagerStoreAccessRule[]>([]);
  const [options, setOptions] = useState<StoreAccessOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<ManagerStoreAccessRule | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [storeOptions, setStoreOptions] = useState<StoreAccessStoreOption[] | null>(null);
  const [preview, setPreview] = useState<StoreAccessPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const loadRules = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const res = await managerStoreAccessAPI.listRules(selectedProjectId);
      setRules(res.data?.data || []);
      setError(null);
    } catch (err) {
      setError(apiMessage(err, 'Failed to load manager access rules.'));
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) return undefined;
    let cancelled = false;
    setLoading(true);
    setStoreOptions(null);
    Promise.all([managerStoreAccessAPI.listRules(selectedProjectId), managerStoreAccessAPI.options(selectedProjectId)])
      .then(([rulesRes, optionsRes]) => {
        if (cancelled) return;
        setRules(rulesRes.data?.data || []);
        setOptions(optionsRes.data?.data || null);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(apiMessage(err, 'Failed to load manager access rules.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  // The full store list is only needed for "specific stores", so it loads the first time
  // that mode is opened rather than with the page.
  useEffect(() => {
    if (!isModalOpen || form.mode !== 'stores' || storeOptions || !selectedProjectId) return undefined;
    let cancelled = false;
    managerStoreAccessAPI.storeOptions(selectedProjectId)
      .then((res) => { if (!cancelled) setStoreOptions(res.data?.data || []); })
      .catch((err) => { if (!cancelled) setFormError(apiMessage(err, 'Failed to load the store list.')); });
    return () => { cancelled = true; };
  }, [isModalOpen, form.mode, storeOptions, selectedProjectId]);

  const criteria = useMemo(() => criteriaFromForm(form), [form]);
  const criteriaJson = JSON.stringify(criteria);

  // Preview what the conditions match, a moment after the last change.
  useEffect(() => {
    if (!isModalOpen || !selectedProjectId) return undefined;
    const current = JSON.parse(criteriaJson) as StoreAccessCriteria;
    if (Object.keys(current).length === 0) {
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(() => {
      managerStoreAccessAPI.preview(selectedProjectId, current)
        .then((res) => {
          if (cancelled) return;
          setPreview(res.data?.data || null);
          setPreviewError(null);
        })
        .catch((err) => {
          if (cancelled) return;
          setPreview(null);
          setPreviewError(apiMessage(err, 'Could not preview these conditions.'));
        })
        .finally(() => { if (!cancelled) setPreviewLoading(false); });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isModalOpen, selectedProjectId, criteriaJson]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setPreview(null);
    setIsModalOpen(true);
  };

  const openEdit = (rule: ManagerStoreAccessRule) => {
    setEditing(rule);
    setForm(formFromRule(rule));
    setFormError(null);
    setPreview(null);
    setIsModalOpen(true);
  };

  const setList = (key: TextListKey, values: string[]) => setForm((f) => ({ ...f, [key]: values }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId) return;
    if (!form.employeeCode) {
      setFormError('Choose the manager this rule is for.');
      return;
    }
    if (Object.keys(criteria).length === 0) {
      setFormError(form.mode === 'stores' ? 'Choose at least one store.' : 'Choose at least one store detail.');
      return;
    }
    setIsSubmitting(true);
    setFormError(null);
    try {
      const payload = { employeeCode: form.employeeCode, label: form.label.trim(), criteria };
      if (editing) {
        await managerStoreAccessAPI.update(selectedProjectId, editing.id, payload);
      } else {
        await managerStoreAccessAPI.create(selectedProjectId, payload);
      }
      setIsModalOpen(false);
      await loadRules();
    } catch (err) {
      setFormError(apiMessage(err, 'Failed to save the rule.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleActive = async (rule: ManagerStoreAccessRule) => {
    if (!selectedProjectId) return;
    try {
      await managerStoreAccessAPI.setActive(selectedProjectId, rule.id, !rule.active);
      await loadRules();
    } catch (err) {
      setError(apiMessage(err, 'Failed to update the rule.'));
    }
  };

  const visibleRules = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rules;
    return rules.filter((rule) =>
      [rule.employeeName, rule.employeeCode, rule.roleName, rule.label]
        .some((value) => (value ?? '').toLowerCase().includes(term)));
  }, [rules, search]);

  const managerOptions = useMemo(() => {
    const list: SearchableOption[] = (options?.managers ?? []).map((m) => ({
      value: m.code,
      label: `${m.name ?? m.code} · ${m.code} · ${m.roleName}`,
    }));
    // A rule for someone no longer offered (left, or moved to a store-team role) must still
    // say who it is for when it is edited.
    if (form.employeeCode && !list.some((o) => o.value === form.employeeCode)) {
      list.unshift({
        value: form.employeeCode,
        label: editing?.employeeName ? `${editing.employeeName} · ${form.employeeCode}` : form.employeeCode,
      });
    }
    return list;
  }, [options, form.employeeCode, editing]);

  const detailPickers: { key: TextListKey; label: string; options: SearchableOption[] }[] = [
    { key: 'tradeIds', label: 'Trade', options: (options?.trades ?? []).map((t) => ({ value: t.id, label: t.active ? t.name : `${t.name} (retired)` })) },
    { key: 'categoryIds', label: 'Store Category', options: (options?.categories ?? []).map((c) => ({ value: String(c.id), label: c.name })) },
    { key: 'regions', label: 'Region', options: textOptions(options?.regions ?? []) },
    { key: 'tiers', label: 'City Tier', options: textOptions(options?.tiers ?? []) },
    { key: 'states', label: 'State', options: textOptions(options?.states ?? []) },
    { key: 'cities', label: 'City', options: textOptions(options?.cities ?? []) },
  ];

  const storePickerOptions = useMemo(
    () => (storeOptions ?? []).map((s) => ({
      value: s.id,
      label: [s.name, s.code, s.city, s.status && s.status !== 'Active' ? s.status : null].filter(Boolean).join(' · '),
    })),
    [storeOptions],
  );

  const addButton = (
    <button
      className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
      onClick={openCreate}
    >
      Add Access Rule
    </button>
  );

  const hasCondition = Object.keys(criteria).length > 0;

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manager Store Access</h1>
        </div>
      )}

      {actionSlot ? createPortal(addButton, actionSlot) : (
        <div className="flex items-center justify-end gap-4">{addButton}</div>
      )}

      {error && <FormErrorBanner message={error} />}

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-6 py-3">
          <p className="text-xs text-gray-500">
            Managers without a rule see only the stores their team works at, exactly as before.
          </p>
          <SearchInput value={search} onChange={setSearch} placeholder="Search manager or rule" />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-6 py-4">Manager</th>
                <th className="px-6 py-4">Gives Access To</th>
                <th className="whitespace-nowrap px-6 py-4" title="Active stores with someone deployed that this rule adds right now">
                  Stores Added
                </th>
                <th className="whitespace-nowrap px-6 py-4">Status</th>
                <th className="whitespace-nowrap px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-40" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-64" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-10" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-14" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                  </tr>
                ))
              ) : visibleRules.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    {rules.length === 0
                      ? 'No access rules yet. Add one to give a manager stores by trade, category, region or a list of stores.'
                      : 'No rules match your search.'}
                  </td>
                </tr>
              ) : (
                visibleRules.map((rule) => {
                  const who = rule.employeeName ?? rule.employeeCode;
                  return (
                    <tr key={rule.id} className={rule.active ? 'hover:bg-gray-50' : 'bg-gray-50/60 text-gray-400'}>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className={`font-medium ${rule.active ? 'text-gray-900' : 'text-gray-500'}`}>{who}</div>
                        <div className="text-xs text-gray-500">
                          {rule.employeeCode}{rule.roleName ? ` · ${rule.roleName}` : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {rule.label && <div className="mb-1 font-medium text-gray-700">{rule.label}</div>}
                        <div className="flex flex-wrap gap-1.5">
                          {describeCriteria(rule.criteria, options).map((part) => (
                            <span key={part} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{part}</span>
                          ))}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-gray-600">{rule.storeCount ?? '—'}</td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {rule.active ? (
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Active</span>
                        ) : (
                          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">Off</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <TableActionGroup>
                          <TableActionButton action="edit" label={`Edit access rule for ${who}`}
                            type="button"
                            onClick={() => openEdit(rule)}
                          />
                          {/* Switched off, never deleted, so who had access when stays on record. */}
                          <TableActionButton action={rule.active ? 'deactivate' : 'activate'} label={`${rule.active ? 'Switch off' : 'Switch on'} access rule for ${who}`}
                            type="button"
                            onClick={() => toggleActive(rule)}
                            title={rule.active
                              ? 'Switch off — the manager goes back to their team stores only'
                              : 'Switch this rule back on'}
                          />
                        </TableActionGroup>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? 'Edit Access Rule' : 'Add Access Rule'}
        size="lg"
        formId="manager-store-access-form"
        isSubmitting={isSubmitting}
        submitLabel="Save"
      >
        <form id="manager-store-access-form" onSubmit={submit} className="space-y-5">
          {formError && <FormErrorBanner message={formError} />}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="msa-manager" className="mb-1 block text-sm font-medium text-gray-700">Manager</label>
              <SearchableSelect
                id="msa-manager"
                value={form.employeeCode}
                onChange={(value) => setForm((f) => ({ ...f, employeeCode: value }))}
                options={managerOptions}
                placeholder="Choose a manager…"
                allowClear={false}
              />
            </div>
            <div>
              <label htmlFor="msa-label" className="mb-1 block text-sm font-medium text-gray-700">
                Rule Name <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                id="msa-label"
                type="text"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="e.g. LFR – South"
                maxLength={100}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">Give access to</span>
            <div className="flex w-fit items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1" role="radiogroup" aria-label="Give access to">
              {([['details', 'Stores matching details'], ['stores', 'Specific stores']] as const).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={form.mode === mode}
                  onClick={() => setForm((f) => ({ ...f, mode }))}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                    form.mode === mode ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {form.mode === 'details' ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                A store must match every detail you pick; within one detail, any of the picked values will do.
                Leave a detail empty to not filter on it.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {detailPickers
                  .filter((picker) => picker.options.length > 0 || form[picker.key].length > 0)
                  .map((picker) => (
                    <div key={picker.key}>
                      <label htmlFor={`msa-${picker.key}`} className="mb-1 block text-sm font-medium text-gray-700">{picker.label}</label>
                      <MultiSearchableSelect
                        id={`msa-${picker.key}`}
                        values={form[picker.key]}
                        onChange={(values) => setList(picker.key, values)}
                        options={optionsWithSelected(picker.options, form[picker.key])}
                        placeholder="Any"
                      />
                    </div>
                  ))}
                {(options?.customFields ?? []).map((field) => (
                  <div key={field.id}>
                    <label htmlFor={`msa-field-${field.id}`} className="mb-1 block text-sm font-medium text-gray-700">{field.label}</label>
                    <MultiSearchableSelect
                      id={`msa-field-${field.id}`}
                      values={form.custom[field.id] ?? []}
                      onChange={(values) => setForm((f) => ({ ...f, custom: { ...f.custom, [field.id]: values } }))}
                      options={optionsWithSelected(textOptions(field.options), form.custom[field.id] ?? [])}
                      placeholder="Any"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="msa-stores" className="mb-1 block text-sm font-medium text-gray-700">Stores</label>
              <MultiSearchableSelect
                id="msa-stores"
                values={form.storeIds}
                onChange={(values) => setForm((f) => ({ ...f, storeIds: values }))}
                options={storePickerOptions}
                placeholder={storeOptions ? 'Choose stores…' : 'Loading stores…'}
                searchPlaceholder="Search by store name, code or city…"
                disabled={!storeOptions}
              />
            </div>
          )}

          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3" aria-live="polite">
            {!hasCondition ? (
              <p className="text-sm text-gray-500">
                {form.mode === 'stores' ? 'Choose stores' : 'Pick store details'} to see which stores this rule covers.
              </p>
            ) : previewError ? (
              <p className="text-sm text-red-600">{previewError}</p>
            ) : previewLoading || !preview ? (
              <p className="text-sm text-gray-500"><InlineLoader label="Checking stores…" /></p>
            ) : (
              <>
                <p className="text-sm text-gray-900">
                  <strong>{preview.visibleStores.toLocaleString()}</strong>{' '}
                  {preview.visibleStores === 1 ? 'store' : 'stores'} will be added for this manager and the managers above them
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {preview.matchedStores.toLocaleString()} match in total. Only Active stores with someone deployed appear in store lists.
                </p>
                {preview.stores.length > 0 && (
                  <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-xs">
                    {preview.stores.map((store) => (
                      <li key={store.id} className={store.willShow ? 'text-gray-700' : 'text-gray-400'}>
                        {[store.name, store.code, store.city].filter(Boolean).join(' · ')}
                        {!store.willShow && ' — not shown (inactive, or nobody deployed)'}
                      </li>
                    ))}
                    {preview.matchedStores > preview.stores.length && (
                      <li className="text-gray-400">…and {(preview.matchedStores - preview.stores.length).toLocaleString()} more</li>
                    )}
                  </ul>
                )}
              </>
            )}
          </div>
        </form>
      </FormModal>
    </div>
  );
}
